import React from 'react';
import { Document, Text, StyleSheet } from '@react-pdf/renderer';
import { CashBankReport, CashMovement, JOURNAL_SOURCE_LABEL } from '../../../types/finance';
import { formatPaisa } from '../../../utils/paisa';
import {
  Page, FilterStrip, StatStrip, DataTable, pdfColors, fmtDate,
  type DataTableColumn, type SchoolBranding,
} from '../../../utils/pdf';
import { Amount, Description, figureWidth, leadColumns } from './movementCells';

interface Props {
  report: CashBankReport;
  currency: string;
  branding: SchoolBranding;
  generatedAt: string;
}

const styles = StyleSheet.create({
  in: { color: pdfColors.success },
  out: { color: pdfColors.danger },
});

/**
 * The Cash & Bank Report — every movement on the school's cash and bank
 * accounts that the screen's filters match, between the balance before the
 * dates and the balance at the end, with money in and out at the top.
 */
const CashBankDocument: React.FC<Props> = ({ report, currency, branding, generatedAt }) => {
  const money = (paisa: number) => formatPaisa(paisa, currency);
  const balance = (paisa: number) => <Text style={paisa < 0 ? styles.out : styles.in}>{money(paisa)}</Text>;
  const figureW = figureWidth([report.openingPaisa, report.closingPaisa, ...report.items.flatMap(m => [m.inPaisa, m.outPaisa])].map(money));
  const columns: Array<DataTableColumn<CashMovement>> = [
    ...leadColumns<CashMovement>(),
    { key: 'description', header: 'Description', flex: 1.6, render: m => <Description text={m.memo} status={m.status} isReversal={m.isReversal} /> },
    { key: 'source', header: 'Source', width: 78, render: m => JOURNAL_SOURCE_LABEL[m.sourceType] ?? m.sourceType },
    { key: 'account', header: 'Account', flex: 1, render: m => `${m.account.code} ${m.account.name}` },
    { key: 'in', header: 'In', width: figureW, align: 'right', render: m => <Amount paisa={m.inPaisa} currency={currency} tone="in" /> },
    { key: 'out', header: 'Out', width: figureW, align: 'right', render: m => <Amount paisa={m.outPaisa} currency={currency} tone="out" /> },
  ];

  return (
    <Document title="Cash & Bank Report" author={branding.name}>
      <Page
        branding={branding}
        orientation="landscape"
        meta={{ title: 'Cash & Bank Report', subtitle: report.filterSummary ?? undefined, generatedAt }}
        official={{ details: [
          { label: 'Records', value: String(report.items.length) },
          { label: 'Generated', value: fmtDate(generatedAt) },
        ] }}
      >
        {report.filterSummary && <FilterStrip summary={report.filterSummary} />}
        <StatStrip
          dense
          items={[
            { label: 'Money in', value: money(report.totals.inPaisa), tone: 'success' },
            { label: 'Money out', value: money(report.totals.outPaisa), tone: 'danger' },
            { label: 'Net', value: money(report.totals.netPaisa), tone: 'primary' },
            { label: 'Movements', value: report.items.length, tone: 'info', weight: 0.7 },
          ]}
        />
        <DataTable
          columns={columns}
          rows={report.items}
          zebra
          compact
          small
          emptyText="No cash or bank movements match the current filters."
          summaryRows={[
            { kind: 'balance', before: true, label: 'Opening balance', values: { in: balance(report.openingPaisa) } },
            { kind: 'balance', label: 'Closing balance', values: { in: balance(report.closingPaisa) } },
          ]}
        />
      </Page>
    </Document>
  );
};

export default CashBankDocument;
